<script setup>
import { ref, computed, onMounted } from "vue";
import api from "../api/axios";
import { useAuth } from "@/composables/useAuth";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonBadge from "@/components/ArgonBadge.vue";

const { user, loadUser } = useAuth();

onMounted(() => {
  loadUser();
});

// =======================
// DATOS DERIVADOS
// =======================
const initials = computed(() => {
  const name = user.value?.name || user.value?.email || "";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");
});

const roleColor = computed(() => {
  switch (user.value?.role) {
    case "admin":
      return "primary";
    case "asesor":
      return "success";
    default:
      return "info";
  }
});

const roleLabel = computed(() => {
  const role = user.value?.role || "";
  return role.charAt(0).toUpperCase() + role.slice(1);
});

// =======================
// CAMBIO DE CONTRASEÑA
// =======================
const passwordForm = ref({
  newPassword: "",
});

const showNew = ref(false);

const saving = ref(false);
const feedback = ref({ type: "", message: "" });

const passwordChecks = computed(() => [
  {
    label: "Mínimo 8 caracteres",
    ok: passwordForm.value.newPassword.length >= 8,
  },
  {
    label: "Al menos una mayúscula",
    ok: /[A-Z]/.test(passwordForm.value.newPassword),
  },
  {
    label: "Al menos un número",
    ok: /\d/.test(passwordForm.value.newPassword),
  },
]);

const canSubmit = computed(
  () => passwordChecks.value.every((c) => c.ok) && !saving.value
);

const resetFeedback = () => {
  feedback.value = { type: "", message: "" };
};

const changePassword = async () => {
  if (!canSubmit.value) return;

  saving.value = true;
  resetFeedback();

  try {
    await api.put("/me/password", {
      newPassword: passwordForm.value.newPassword,
    });

    feedback.value = {
      type: "success",
      message: "✅ Contraseña actualizada correctamente.",
    };

    passwordForm.value = {
      newPassword: "",
    };
  } catch (err) {
    feedback.value = {
      type: "danger",
      message:
        err.response?.data?.error ||
        "No se pudo actualizar la contraseña. Intenta de nuevo.",
    };
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="py-4 container-fluid">
    <!-- ===== ENCABEZADO CON GRADIENTE ===== -->
    <div class="card perfil-header overflow-hidden mb-4">
      <div class="perfil-header-bg bg-gradient-primary"></div>

      <div class="card-body position-relative d-flex flex-wrap align-items-end gap-3 pt-0">
        <div class="perfil-avatar shadow">
          <span>{{ initials || "?" }}</span>
        </div>

        <div class="flex-grow-1 pb-2">
          <h4 class="mb-0">{{ user?.name || "Cargando..." }}</h4>
          <p class="text-sm text-secondary mb-0">
            <i class="ni ni-email-83 me-1"></i>{{ user?.email }}
          </p>
        </div>

        <div class="pb-2">
          <argon-badge :color="roleColor" variant="gradient" size="lg">
            <i class="ni ni-badge me-1"></i>{{ roleLabel }}
          </argon-badge>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- ===== INFORMACIÓN DE LA CUENTA ===== -->
      <div class="col-lg-5 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6 class="mb-0">
              <i class="ni ni-single-02 text-primary me-2"></i>Información de la cuenta
            </h6>
            <p class="text-xs text-secondary mb-0">
              Estos datos son gestionados por el administrador.
            </p>
          </div>

          <div class="card-body pt-3">
            <ul class="list-group list-group-flush">
              <li class="list-group-item px-0 d-flex align-items-center">
                <div class="perfil-icon bg-gradient-primary me-3">
                  <i class="ni ni-circle-08 text-white"></i>
                </div>
                <div>
                  <span class="text-xs text-secondary d-block">Nombre de usuario</span>
                  <span class="text-sm font-weight-bold">{{ user?.name || "—" }}</span>
                </div>
              </li>

              <li class="list-group-item px-0 d-flex align-items-center">
                <div class="perfil-icon bg-gradient-success me-3">
                  <i class="ni ni-email-83 text-white"></i>
                </div>
                <div>
                  <span class="text-xs text-secondary d-block">Correo electrónico</span>
                  <span class="text-sm font-weight-bold">{{ user?.email || "—" }}</span>
                </div>
              </li>

              <li class="list-group-item px-0 d-flex align-items-center border-0">
                <div class="perfil-icon bg-gradient-info me-3">
                  <i class="ni ni-badge text-white"></i>
                </div>
                <div>
                  <span class="text-xs text-secondary d-block">Rol asignado</span>
                  <span class="text-sm font-weight-bold">{{ roleLabel || "—" }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ===== SEGURIDAD / CAMBIO DE CONTRASEÑA ===== -->
      <div class="col-lg-7 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6 class="mb-0">
              <i class="ni ni-lock-circle-open text-primary me-2"></i>Seguridad
            </h6>
            <p class="text-xs text-secondary mb-0">
              Actualiza tu contraseña periódicamente para proteger tu cuenta.
            </p>
          </div>

          <div class="card-body pt-3">
            <div
              v-if="feedback.message"
              class="alert text-white text-sm py-2"
              :class="`alert-${feedback.type}`"
              role="alert"
            >
              {{ feedback.message }}
            </div>

            <form @submit.prevent="changePassword">
              <label class="form-label text-sm">Nueva contraseña</label>
              <div class="input-group mb-3">
                <input
                  v-model="passwordForm.newPassword"
                  :type="showNew ? 'text' : 'password'"
                  class="form-control"
                  placeholder="Nueva contraseña"
                  autocomplete="new-password"
                  @input="resetFeedback"
                />
                <span class="input-group-text cursor-pointer" @click="showNew = !showNew">
                  <i class="fas" :class="showNew ? 'fa-eye-slash' : 'fa-eye'"></i>
                </span>
              </div>

              <!-- Checklist de requisitos -->
              <ul class="list-unstyled mb-3">
                <li
                  v-for="check in passwordChecks"
                  :key="check.label"
                  class="text-xs d-flex align-items-center mb-1"
                  :class="check.ok ? 'text-success' : 'text-secondary'"
                >
                  <i
                    class="me-2"
                    :class="check.ok ? 'fas fa-check-circle' : 'far fa-circle'"
                  ></i>
                  {{ check.label }}
                </li>
              </ul>

              <argon-button
                color="primary"
                variant="gradient"
                :disabled="!canSubmit"
                full-width
              >
                <span v-if="saving">
                  <span class="spinner-border spinner-border-sm me-2"></span>Guardando...
                </span>
                <span v-else>
                  <i class="ni ni-check-bold me-1"></i>Actualizar contraseña
                </span>
              </argon-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.perfil-header-bg {
  height: 110px;
  margin: -1px -1px 0 -1px;
  border-radius: 1rem 1rem 0 0;
}

.perfil-avatar {
  width: 84px;
  height: 84px;
  margin-top: -42px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(310deg, #5e72e4 0%, #825ee4 100%);
}

.perfil-avatar span {
  color: #fff;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.perfil-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
